import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';

const SEVERITY_META = {
  critical: { label: 'Critical', dot: 'bg-red-500',    badge: 'bg-red-100 text-red-700 border-red-200',    border: 'border-l-red-500',    score: 10 },
  high:     { label: 'High',     dot: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700 border-amber-200',  border: 'border-l-amber-500',  score: 7  },
  medium:   { label: 'Medium',   dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200', border: 'border-l-yellow-400', score: 4  },
  low:      { label: 'Low',      dot: 'bg-blue-400',   badge: 'bg-blue-100 text-blue-700 border-blue-200',   border: 'border-l-blue-400',   score: 1  },
};

const RULE_CATEGORIES = {
  cancelled_with_payments:   'Financial Integrity',
  value_mismatch:            'Financial Integrity',
  overbilled:                'Financial Integrity',
  status_should_complete:    'Status Accuracy',
  cancelled_live_milestones: 'Financial Integrity',
  ready_to_bill_stale:       'Billing',
  pass_through_not_invoiced: 'Billing',
  issued_stale:              'Pipeline',
  active_no_milestones:      'Data Completeness',
  missing_forecast:          'Data Completeness',
  missing_pm:                'Data Completeness',
  no_delivery_owner:         'Data Completeness',
  zero_amount_milestone:     'Data Completeness',
  missing_contract_date:     'Data Completeness',
  duplicate_milestone_code:  'Data Quality',
  duplicate_legacy_line:     'Data Quality',
  total_data_repair:         'Financial Integrity',
  total_unverified_legacy:   'Financial Integrity',
  legacy_value_mismatch:     'Financial Integrity',
};

function ScoreBadge({ score }) {
  const cls = score >= 20 ? 'bg-red-500 text-white'
    : score >= 10 ? 'bg-amber-500 text-amber-950'
    : score >= 5  ? 'bg-yellow-400 text-gray-900'
    : 'bg-gray-200 text-gray-600';
  return (
    <span className={`inline-flex items-center justify-center min-w-[28px] h-6 px-1.5 rounded-md text-[10px] font-extrabold tabular-nums ${cls}`}>
      {score}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const meta = SEVERITY_META[severity] || SEVERITY_META.low;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wide ${meta.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function SowTag({ status }) {
  const cls = {
    Active:    'bg-emerald-100 text-emerald-700',
    Issued:    'bg-blue-100 text-blue-700',
    'On Hold': 'bg-amber-100 text-amber-700',
    Complete:  'bg-gray-100 text-gray-500',
    Cancelled: 'bg-red-100 text-red-600',
    Expired:   'bg-orange-100 text-orange-700',
    Draft:     'bg-slate-100 text-slate-500',
  }[status] || 'bg-gray-100 text-gray-500';
  return <span className={`px-1.5 py-px rounded text-[8px] font-bold ${cls}`}>{status}</span>;
}

// Inline suppress form — appears inside the issue row
function SuppressForm({ onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="mt-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg" onClick={e => e.stopPropagation()}>
      <div className="text-[8px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
        Reason for ignoring <span className="text-red-400">*</span>
        <span className="ml-1 font-normal normal-case text-slate-400">— explain why this issue won't be fixed (e.g. "legacy data from 2023 migration")</span>
      </div>
      <div className="flex gap-2">
        <input
          ref={ref}
          value={reason}
          onChange={e => setReason(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && reason.trim()) onConfirm(reason); if (e.key === 'Escape') onCancel(); }}
          placeholder="Enter reason…"
          className="form-input flex-1 text-[10px] py-1"
        />
        <button
          onClick={() => reason.trim() && onConfirm(reason)}
          disabled={!reason.trim()}
          className="px-2.5 py-1 text-[9px] font-semibold bg-slate-600 text-white rounded disabled:opacity-40 whitespace-nowrap"
        >Ignore permanently</button>
        <button onClick={onCancel} className="px-2.5 py-1 text-[9px] border border-gray-200 rounded text-gray-500 hover:bg-gray-50 whitespace-nowrap">Cancel</button>
      </div>
    </div>
  );
}

export default function SOWValidationPage() {
  const { navigate: _navigate } = useApp();
  const openSow = (sowId) => window.open(`#sow-detail/${sowId}`, '_blank');
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [viewMode,       setViewMode]       = useState('by-rule');
  const [sevFilter,      setSevFilter]      = useState('all');
  const [catFilter,      setCatFilter]      = useState('all');
  const [search,         setSearch]         = useState('');
  const [showIgnored,    setShowIgnored]    = useState(false);
  const [suppressingKey, setSuppressingKey] = useState(null); // "sowId::ruleId"
  const [fixingId,       setFixingId]       = useState(null); // sow_id being fixed
  const [bulkRunning,    setBulkRunning]    = useState(false); // bulk legacy-line dedupe in progress
  const [expandedRules,  setExpandedRules]  = useState(new Set());
  const [expandedSows,   setExpandedSows]   = useState(new Set());
  // Every finding quotes the SOW's values, so the server answers only holders
  // of sow:see-financials as well as this page. The duplicate-line clean-ups
  // delete SOW lines, so they are sow:edit too.
  const seesMoney   = canAccess('sow:see-financials');
  const canEditSows = canAccess('sow:edit');

  const load = useCallback(async () => {
    if (!seesMoney) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const result = await api.get('validation');
      setData(result);
      if (result?.issues) {
        const autoExpand = new Set(
          result.issues
            .filter(i => !i.suppressed && (i.severity === 'critical' || i.severity === 'high'))
            .map(i => i.rule_id)
        );
        setExpandedRules(autoExpand);
      }
    } catch (e) {
      setError(e.message || 'Failed to load validation data');
    } finally {
      setLoading(false);
    }
  }, [seesMoney]);

  useEffect(() => { load(); }, [load]);

  const suppress = async (issue, reason) => {
    setSuppressingKey(null);
    await api.post('validation/suppress', { sow_id: issue.sow_id, rule_id: issue.rule_id, reason });
    await load();
  };

  const unsuppress = async (suppressionId) => {
    await api.delete(`validation/suppress/${suppressionId}`);
    await load();
  };

  // Bulk-remove duplicate legacy-extraction lines across every affected SOW.
  // Dry-runs first to get the counts, confirms, then sweeps (server caps per call
  // and returns `remaining`, so we loop until the book is clean).
  const runBulkDedupe = async () => {
    const gbp = (n) => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setBulkRunning(true);
    try {
      const preview = await api.post('validation/dedupe-legacy-lines', { dry_run: true });
      if (!preview?.sows) { window.alert('No duplicate legacy-extraction lines found.'); return; }
      const ok = window.confirm(
        `Remove ${preview.lines} duplicate line${preview.lines !== 1 ? 's' : ''} across ${preview.sows} SOW${preview.sows !== 1 ? 's' : ''}?\n\n` +
        `This inflates the resource/hardware roll-up by ${gbp(preview.value)}. Only AI-imported (legacy) duplicate lines are removed — manually-entered lines and contract totals are untouched. This cannot be undone.`
      );
      if (!ok) return;
      let totalRemoved = 0, totalSows = 0, guard = 0;
      // Server caps each call; keep going while it reports more remaining.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await api.post('validation/dedupe-legacy-lines', {});
        totalRemoved += res?.lines || 0;
        totalSows += res?.sows || 0;
        if (!res?.remaining || res.remaining <= 0 || res.sows === 0 || ++guard > 50) break;
      }
      await load();
      window.alert(`Removed ${totalRemoved} duplicate line${totalRemoved !== 1 ? 's' : ''} across ${totalSows} SOW${totalSows !== 1 ? 's' : ''}.`);
    } catch (e) {
      window.alert('Bulk dedupe failed: ' + (e?.message || e));
    } finally {
      setBulkRunning(false);
    }
  };

  // Quick-fix actions for specific fixable rules
  const FIXABLE_RULES = {
    status_should_complete: {
      label: 'Mark as Complete',
      icon: '✓',
      cls: 'text-emerald-700 border-emerald-300 hover:bg-emerald-50',
      run: async (issue) => {
        setFixingId(issue.sow_id);
        try {
          await api.put(`sows/${issue.sow_id}`, { status: 'Complete', previous_status: issue.sow_status });
          await load();
        } finally {
          setFixingId(null);
        }
      },
    },
    cancelled_with_payments: {
      label: 'Change to Complete',
      icon: '✓',
      cls: 'text-emerald-700 border-emerald-300 hover:bg-emerald-50',
      run: async (issue) => {
        setFixingId(issue.sow_id);
        try {
          await api.put(`sows/${issue.sow_id}`, { status: 'Complete', previous_status: issue.sow_status });
          await load();
        } finally {
          setFixingId(null);
        }
      },
    },
    duplicate_legacy_line: {
      label: 'Remove duplicates',
      icon: '🧹',
      cls: 'text-blue-700 border-blue-300 hover:bg-blue-50',
      run: async (issue) => {
        setFixingId(issue.sow_id);
        try {
          await api.post(`sows/${issue.sow_id}/dedupe-legacy-lines`, {});
          await load();
        } finally {
          setFixingId(null);
        }
      },
    },
  };
  // The server refuses a move to Complete without this grant, so do not offer it.
  if (!canAccess('sow:complete')) { delete FIXABLE_RULES.status_should_complete; delete FIXABLE_RULES.cancelled_with_payments; }
  if (!canEditSows) delete FIXABLE_RULES.duplicate_legacy_line;

  // Issues to display (active ones, or all if showIgnored)
  const visibleIssues = (data?.issues || []).filter(i => showIgnored || !i.suppressed);

  const filtered = visibleIssues.filter(i => {
    if (sevFilter !== 'all' && i.severity !== sevFilter) return false;
    if (catFilter !== 'all' && RULE_CATEGORIES[i.rule_id] !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.sow_ref?.toLowerCase().includes(q) &&
          !i.sow_detail?.toLowerCase().includes(q) &&
          !i.client_name?.toLowerCase().includes(q) &&
          !i.rule_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Group by rule
  const byRule = {};
  for (const issue of filtered) {
    if (!byRule[issue.rule_id]) byRule[issue.rule_id] = { rule_id: issue.rule_id, rule_name: issue.rule_name, severity: issue.severity, score: issue.score, items: [] };
    byRule[issue.rule_id].items.push(issue);
  }
  const ruleGroups = Object.values(byRule).sort((a, b) => {
    // Active issues before suppressed, then by score
    const aActive = a.items.filter(i => !i.suppressed).length;
    const bActive = b.items.filter(i => !i.suppressed).length;
    if (bActive !== aActive) return bActive - aActive;
    return b.score - a.score;
  });

  // Group by SOW
  const bySow = {};
  for (const issue of filtered) {
    if (!bySow[issue.sow_id]) bySow[issue.sow_id] = { sow_id: issue.sow_id, sow_ref: issue.sow_ref, sow_detail: issue.sow_detail, sow_status: issue.sow_status, client_name: issue.client_name, items: [], totalScore: 0 };
    bySow[issue.sow_id].items.push(issue);
    if (!issue.suppressed) bySow[issue.sow_id].totalScore += issue.score;
  }
  const sowGroups = Object.values(bySow).sort((a, b) => b.totalScore - a.totalScore);

  const categories = [...new Set(Object.values(RULE_CATEGORIES))].sort();
  const summary = data?.summary;
  const suppressedCount = data?.summary?.suppressed || 0;

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' · ' +
           d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const toggleRule = (id) => setExpandedRules(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSow  = (id) => setExpandedSows (s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Say what is missing rather than load a page the server will refuse.
  if (!seesMoney) {
    return (
      <div className="fade-in">
        <h1 className="text-base font-extrabold text-navy mb-4">SOW & Milestone Validation</h1>
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800">
          The validation checks quote SOW values, so this page also needs the
          {' '}<strong>See SOW values, rates, costs and contractor pay</strong> permission.
          An administrator can grant it in Admin → Access.
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-base font-extrabold text-navy">SOW & Milestone Validation</h1>
          </div>
          {summary && (
            <p className="text-[9px] text-gray-400 mt-0.5">
              Last run {fmtTime(summary.generated_at)} · {summary.sows_affected} SOW{summary.sows_affected !== 1 ? 's' : ''} affected
              {suppressedCount > 0 && <> · <span className="text-slate-400">{suppressedCount} ignored</span></>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          {canEditSows && (data?.issues || []).some(i => i.rule_id === 'duplicate_legacy_line' && !i.suppressed) && (
            <button onClick={runBulkDedupe} disabled={bulkRunning || loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
              title="Remove every duplicate legacy-extraction line across all affected SOWs in one sweep (previews the count first).">
              🧹 {bulkRunning ? 'Removing…' : 'Remove all duplicate legacy lines'}
            </button>
          )}
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'animate-spin' : ''}><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            {loading ? 'Running…' : 'Re-run checks'}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
          {[
            { label: 'Active Issues', value: summary.total, cls: 'bg-navy text-white', sub: `${summary.sows_affected} SOWs` },
            { label: 'Critical', value: summary.critical, cls: summary.critical > 0 ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400', sub: '10 pts each' },
            { label: 'High',     value: summary.high,     cls: summary.high     > 0 ? 'bg-amber-500 text-amber-950' : 'bg-gray-100 text-gray-400', sub: '7 pts each' },
            { label: 'Medium',   value: summary.medium,   cls: summary.medium   > 0 ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-400', sub: '4 pts each' },
            { label: 'Low',      value: summary.low,      cls: summary.low      > 0 ? 'bg-blue-400 text-blue-950' : 'bg-gray-100 text-gray-400', sub: '1 pt each' },
          ].map(s => (
            <div key={s.label} className={`rounded-lg px-3 py-2.5 flex flex-col ${s.cls}`}>
              <span className="text-[9px] font-bold uppercase tracking-wider opacity-80">{s.label}</span>
              <span className="text-2xl font-extrabold leading-tight tabular-nums">{s.value}</span>
              <span className="text-[8px] opacity-70">{s.sub}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-[10px] text-red-700">{error}</div>
      )}

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {['all', 'critical', 'high', 'medium', 'low'].map(s => (
            <button key={s} onClick={() => setSevFilter(s)}
              className={`px-2.5 py-1 text-[9px] font-semibold rounded-md transition-colors capitalize ${sevFilter === s ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-gray-700'}`}>
              {s === 'all' ? 'All' : s}
              {s !== 'all' && summary && <span className="ml-1 opacity-60">({summary[s]})</span>}
            </button>
          ))}
        </div>

        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="form-select text-[9px] py-1">
          <option value="all">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SOW, client, rule…" className="form-input text-[9px] py-1 w-44" />

        {/* Ignored toggle */}
        {suppressedCount > 0 && (
          <button
            onClick={() => setShowIgnored(v => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-semibold rounded-lg border transition-colors ${showIgnored ? 'bg-slate-700 border-slate-700 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {showIgnored
                ? <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                : <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
              }
            </svg>
            {showIgnored ? 'Hide ignored' : `Show ignored (${suppressedCount})`}
          </button>
        )}

        <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {[['by-rule', 'By Rule'], ['by-sow', 'By SOW']].map(([v, l]) => (
            <button key={v} onClick={() => setViewMode(v)}
              className={`px-2.5 py-1 text-[9px] font-semibold rounded-md transition-colors ${viewMode === v ? 'bg-white shadow-sm text-navy' : 'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <div className="py-16 text-center text-[10px] text-gray-400">
          <div className="inline-block w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mb-3" />
          <div>Running validation checks…</div>
        </div>
      )}

      {!loading && filtered.length === 0 && data && (
        <div className="py-16 text-center">
          <div className="text-4xl mb-3">✅</div>
          <div className="text-sm font-bold text-gray-700">No issues found</div>
          <div className="text-[10px] text-gray-400 mt-1">
            {sevFilter !== 'all' || catFilter !== 'all' || search ? 'Try adjusting the filters'
              : suppressedCount > 0 && !showIgnored ? `All active issues resolved — ${suppressedCount} ignored`
              : 'All validation checks passed'}
          </div>
        </div>
      )}

      {/* BY RULE view */}
      {viewMode === 'by-rule' && !loading && ruleGroups.length > 0 && (
        <div className="space-y-2">
          {ruleGroups.map(group => {
            const meta = SEVERITY_META[group.severity];
            const isOpen = expandedRules.has(group.rule_id);
            const cat = RULE_CATEGORIES[group.rule_id] || 'Other';
            const activeCount = group.items.filter(i => !i.suppressed).length;
            const ignoredCount = group.items.filter(i => i.suppressed).length;
            const allSuppressed = activeCount === 0;
            return (
              <div key={group.rule_id} className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${allSuppressed ? 'opacity-60' : ''}`}>
                <button
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors border-l-4 ${allSuppressed ? 'border-l-gray-300' : meta.border} text-left`}
                  onClick={() => toggleRule(group.rule_id)}
                >
                  <ScoreBadge score={group.score * activeCount} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-bold ${allSuppressed ? 'text-gray-400 line-through' : 'text-navy'}`}>{group.rule_name}</span>
                      {!allSuppressed && <SeverityBadge severity={group.severity} />}
                      <span className="text-[8px] text-gray-400 border border-gray-200 rounded px-1.5 py-px">{cat}</span>
                    </div>
                    <div className="text-[9px] text-gray-500 mt-0.5">
                      {activeCount > 0 && <>{activeCount} SOW{activeCount !== 1 ? 's' : ''} affected · {group.score} pts per issue</>}
                      {ignoredCount > 0 && <span className="ml-2 text-slate-400">{ignoredCount} ignored</span>}
                    </div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {isOpen && (
                  <div className="divide-y divide-gray-50">
                    {group.items.map((issue, idx) => {
                      const key = `${issue.sow_id}::${issue.rule_id}`;
                      const isSupressing = suppressingKey === key;
                      return (
                        <div key={idx} className={`px-4 py-2 ${issue.suppressed ? 'bg-slate-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-start gap-3 cursor-pointer" onClick={() => !issue.suppressed && openSow(issue.sow_id)}>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <div className={`flex items-center gap-1.5 flex-wrap ${issue.suppressed ? 'opacity-50' : ''}`}>
                                <span className="text-[10px] font-bold text-navy font-mono">{issue.sow_ref}</span>
                                {issue.milestone_code && <span className="text-[9px] font-mono text-gray-500">· {issue.milestone_code}</span>}
                                <SowTag status={issue.sow_status} />
                                {issue.client_name && <span className="text-[9px] text-gray-400">{issue.client_name}</span>}
                              </div>
                              {issue.sow_detail && <div className={`text-[9px] text-gray-500 truncate ${issue.suppressed ? 'opacity-50' : ''}`}>{issue.sow_detail}</div>}
                              <div className={`text-[9px] mt-0.5 ${issue.suppressed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{issue.detail}</div>
                              {issue.suppressed && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 flex-shrink-0"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                  <span className="text-[9px] text-slate-500 italic">Ignored: {issue.suppression_reason}</span>
                                  {issue.suppressed_by_name && <span className="text-[8px] text-slate-400">· by {issue.suppressed_by_name}</span>}
                                </div>
                              )}
                            </div>
                            {!issue.suppressed && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-300 flex-shrink-0 mt-1"><polyline points="9 18 15 12 9 6"/></svg>
                            )}
                          </div>
                          {/* Fix / suppress / unsuppress controls */}
                          <div className="mt-1.5 flex items-center gap-3">
                            {!issue.suppressed && FIXABLE_RULES[issue.rule_id] && (
                              <button
                                onClick={e => { e.stopPropagation(); FIXABLE_RULES[issue.rule_id].run(issue); }}
                                disabled={fixingId === issue.sow_id}
                                className={`text-[9px] font-semibold px-2 py-0.5 rounded border flex items-center gap-1 disabled:opacity-50 ${FIXABLE_RULES[issue.rule_id].cls}`}
                              >
                                {fixingId === issue.sow_id ? '…' : FIXABLE_RULES[issue.rule_id].icon} {FIXABLE_RULES[issue.rule_id].label}
                              </button>
                            )}
                            {!issue.suppressed && !isSupressing && (
                              <button
                                onClick={e => { e.stopPropagation(); setSuppressingKey(key); }}
                                className="text-[9px] text-slate-400 hover:text-slate-600 flex items-center gap-1"
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                Ignore permanently
                              </button>
                            )}
                            {issue.suppressed && (
                              <button
                                onClick={() => unsuppress(issue.suppression_id)}
                                className="text-[9px] text-amber-600 hover:text-amber-800 flex items-center gap-1"
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                Un-ignore
                              </button>
                            )}
                          </div>
                          {isSupressing && (
                            <SuppressForm
                              onConfirm={reason => suppress(issue, reason)}
                              onCancel={() => setSuppressingKey(null)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* BY SOW view */}
      {viewMode === 'by-sow' && !loading && sowGroups.length > 0 && (
        <div className="space-y-2">
          {sowGroups.map(sow => {
            const isOpen = expandedSows.has(sow.sow_id);
            const activeItems = sow.items.filter(i => !i.suppressed);
            const ignoredItems = sow.items.filter(i => i.suppressed);
            const worstSev = activeItems.some(i => i.severity === 'critical') ? 'critical'
              : activeItems.some(i => i.severity === 'high') ? 'high'
              : activeItems.some(i => i.severity === 'medium') ? 'medium'
              : activeItems.length > 0 ? 'low' : null;
            const meta = worstSev ? SEVERITY_META[worstSev] : SEVERITY_META.low;
            return (
              <div key={sow.sow_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors border-l-4 ${worstSev ? meta.border : 'border-l-gray-200'} text-left`}
                  onClick={() => toggleSow(sow.sow_id)}
                >
                  <ScoreBadge score={sow.totalScore} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold text-navy font-mono">{sow.sow_ref}</span>
                      <SowTag status={sow.sow_status} />
                      {sow.client_name && <span className="text-[9px] text-gray-500">{sow.client_name}</span>}
                    </div>
                    <div className="text-[9px] text-gray-500 truncate">{sow.sow_detail}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {['critical','high','medium','low'].map(s => {
                        const n = activeItems.filter(i => i.severity === s).length;
                        if (!n) return null;
                        return <SeverityBadge key={s} severity={s} />;
                      })}
                      {activeItems.length > 0 && <span className="text-[8px] text-gray-400">{activeItems.length} issue{activeItems.length !== 1 ? 's' : ''}</span>}
                      {ignoredItems.length > 0 && <span className="text-[8px] text-slate-400">{ignoredItems.length} ignored</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={e => { e.stopPropagation(); openSow(sow.sow_id); }} className="px-2 py-0.5 text-[9px] border border-navy/20 text-navy rounded hover:bg-navy/5">View SOW ↗</button>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-gray-50">
                    {sow.items.sort((a, b) => (a.suppressed ? 1 : -1) - (b.suppressed ? 1 : -1) || b.score - a.score).map((issue, idx) => {
                      const key = `${issue.sow_id}::${issue.rule_id}`;
                      const isSupressing = suppressingKey === key;
                      return (
                        <div key={idx} className={`px-4 py-2 ${issue.suppressed ? 'bg-slate-50' : ''}`}>
                          <div className="flex items-start gap-3">
                            <SeverityBadge severity={issue.severity} />
                            <div className="flex-1 min-w-0">
                              <div className={`text-[9px] font-semibold ${issue.suppressed ? 'text-gray-400 line-through' : 'text-navy'}`}>{issue.rule_name}</div>
                              <div className={`text-[9px] mt-0.5 ${issue.suppressed ? 'text-gray-400 line-through' : 'text-gray-600'}`}>{issue.detail}</div>
                              {issue.suppressed && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className="text-[9px] text-slate-400 italic">Ignored: {issue.suppression_reason}</span>
                                  {issue.suppressed_by_name && <span className="text-[8px] text-slate-400">· {issue.suppressed_by_name}</span>}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {!issue.suppressed && <ScoreBadge score={issue.score} />}
                              {!issue.suppressed && FIXABLE_RULES[issue.rule_id] && (
                                <button
                                  onClick={() => FIXABLE_RULES[issue.rule_id].run(issue)}
                                  disabled={fixingId === issue.sow_id}
                                  className={`text-[9px] font-semibold px-2 py-0.5 rounded border disabled:opacity-50 whitespace-nowrap ${FIXABLE_RULES[issue.rule_id].cls}`}
                                >
                                  {fixingId === issue.sow_id ? '…' : FIXABLE_RULES[issue.rule_id].icon} {FIXABLE_RULES[issue.rule_id].label}
                                </button>
                              )}
                              {!issue.suppressed && !isSupressing && (
                                <button onClick={() => setSuppressingKey(key)} className="text-[9px] text-slate-400 hover:text-slate-600 whitespace-nowrap">Ignore</button>
                              )}
                              {issue.suppressed && (
                                <button onClick={() => unsuppress(issue.suppression_id)} className="text-[9px] text-amber-600 hover:text-amber-800 whitespace-nowrap">Un-ignore</button>
                              )}
                            </div>
                          </div>
                          {isSupressing && (
                            <SuppressForm
                              onConfirm={reason => suppress(issue, reason)}
                              onCancel={() => setSuppressingKey(null)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {data && filtered.length > 0 && (
        <div className="mt-6 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-[8px] font-bold uppercase tracking-wider text-gray-400 mb-2">Scoring guide</div>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {Object.entries(SEVERITY_META).map(([s, meta]) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                <span className="text-[9px] text-gray-600"><span className="font-semibold capitalize">{s}</span> — {meta.score} pt{meta.score !== 1 ? 's' : ''}</span>
              </div>
            ))}
            <span className="text-[9px] text-gray-400">· Ignored issues are hidden from counts but visible via "Show ignored"</span>
          </div>
        </div>
      )}
    </div>
  );
}
